/* eslint-disable react/no-this-in-sfc */
import { TooltipFormatterContextObject } from 'highcharts';
import { round } from 'lodash';
import moment from 'moment';
import React, { FC, useMemo } from 'react';
import { TableSkeleton } from 'components/atoms/table';
import { Filter } from 'components/molecules/filters/types';
import { WidgetContainer } from 'components/organisms/projects-report/common';
import { DateRange } from 'components/organisms/projects-report/common/range-picker/types';
import { TimelineViewTable } from 'components/organisms/projects-report/timeline-view/table';
import { useProjectsReport } from 'components/pages/projects-report/use-projects-report';
import { useTranslate } from 'hooks/use-translation';
import {
  ChartWithZoom,
  ChartZoomLevel,
  ZoomChartOption,
} from '../../chart-with-zoom';
import { Wrapper } from '../../components';
import { DefaultEmptyChart, DefaultEmptyTable } from '../../empty-state';
import { TemplateInsightTooltip } from '../../template-insight-tooltip';
import {
  DeviationMeta,
  Epoch,
  getLineChartOption,
  getProjectRecordsByTemplate,
  useWidgetTransformer,
} from '../../utils';
import { getTemplateIdFromFilter } from '../../utils/common';
import { TemplateInsightWidget } from '../widgets';
import { useProjectWidgetInTemplateInsight } from './use-project-widget';

const getTooltipFormatter =
  (tooltip: (val: number) => string) =>
  (
    option: TooltipFormatterContextObject,
    zoomLevel: ChartZoomLevel,
    deviationMetaByZoomLevel: Record<
      string,
      Record<ChartZoomLevel, Record<Epoch, DeviationMeta>>
    >,
  ) => {
    const epoch = option.point.x;
    const delayedBy = option.point.y;

    if (!epoch || delayedBy == null) {
      return '';
    }
    const timeFormatter =
      zoomLevel === ChartZoomLevel.Monthly ? 'MMM-YYYY' : 'DD-MMM';
    const time = moment(epoch).format(timeFormatter);
    const { name } = option.series;
    const deviationDetails = deviationMetaByZoomLevel[name][zoomLevel][epoch];

    if (!deviationDetails) {
      return '';
    }
    const noOfItems = deviationDetails.deviation.length;

    return TemplateInsightTooltip({
      title: tooltip(noOfItems),
      delayedBy,
      time,
    });
  };

export const ByProject: FC<{
  range: DateRange;
  filter: Filter;
}> = ({ range, filter }) => {
  const translate = useTranslate();
  const isInitialized = !!getTemplateIdFromFilter(filter);
  const projectsReport = useProjectsReport(range, filter, isInitialized);
  const records = useMemo(
    () => getProjectRecordsByTemplate(projectsReport),
    [projectsReport],
  );

  const chartOptions: ZoomChartOption = useMemo(() => {
    const deviationMetaByZoomLevel = records.reduce<
      Record<ChartZoomLevel, Record<Epoch, DeviationMeta>>
    >(
      (acc, record) => {
        const week = moment(record.startDateActual).startOf('week').valueOf();
        const month = moment(record.startDateActual).startOf('month').valueOf();

        if (typeof record.durationDifference !== 'number') {
          return acc;
        }
        const durationDiff = round(record.durationDifference);
        const newWeekDeviation = (acc.weekly[week]?.deviation ?? []).concat(
          durationDiff,
        );

        const newMonthDeviation = (acc.monthly[month]?.deviation ?? []).concat(
          durationDiff,
        );

        const toSum = (sum: number, value: number) => sum + value;

        acc.weekly[week] = {
          deviation: newWeekDeviation,
          average: round(
            newWeekDeviation.reduce(toSum, 0) / newWeekDeviation.length,
          ),
        };
        acc.monthly[month] = {
          deviation: newMonthDeviation,
          average: round(
            newMonthDeviation.reduce(toSum, 0) / newMonthDeviation.length,
          ),
        };

        return acc;
      },
      { weekly: {}, monthly: {} },
    );

    return {
      monthly: getLineChartOption(
        ChartZoomLevel.Monthly,
        { default: deviationMetaByZoomLevel },
        translate,
        {
          xAxisTitle: translate(
            'reports.template_insight.by-template.by-project.chart.xAxis',
          ),
          tooltipFormatter: getTooltipFormatter((val) =>
            translate('reports.template_insight.by-template.tooltip.project', {
              count: val,
            }),
          ),
          showLegend: false,
        },
      ),
      weekly: getLineChartOption(
        ChartZoomLevel.Weekly,
        { default: deviationMetaByZoomLevel },
        translate,
        {
          xAxisTitle: translate(
            'reports.template_insight.by-template.by-project.chart.xAxis',
          ),
          tooltipFormatter: getTooltipFormatter((val) =>
            translate('reports.template_insight.by-template.tooltip.project', {
              count: val,
            }),
          ),
          showLegend: false,
        },
      ),
    };
  }, [records, translate]);

  const { compareInfo, currentWidgetInfo } = useProjectWidgetInTemplateInsight({
    currentProjectReport: projectsReport,
    range,
    filter,
  });
  const {
    averageCompletionDelay,
    averageStartingDelay,
    avgDuration,
    noOfProjects,
    percentageCompletedOnTime,
    percentageStartedOnTime,
  } = currentWidgetInfo;
  const widgetTransformer = useWidgetTransformer();

  return (
    <Wrapper>
      <WidgetContainer xs={24} md={8} lg={4} xl={4} xxl={4}>
        <TemplateInsightWidget
          value={noOfProjects}
          title={translate(
            'reports.template_insight.widget.no_of_projects.title',
          )}
          tooltip={translate(
            'reports.template_insight.widget.no_of_projects.description',
          )}
          noDataMsg="No Data"
          cmpValue={compareInfo?.noOfProjects}
          headerTransformer={widgetTransformer.plainText}
          tooltipTransformer={widgetTransformer.plainText}
        />
        <TemplateInsightWidget
          value={avgDuration}
          title={translate(
            'reports.template_insight.widget.avg_duration.title',
          )}
          tooltip={translate(
            'reports.template_insight.widget.avg_duration.description',
          )}
          noDataMsg="No Data"
          cmpValue={compareInfo?.avgDuration}
          headerTransformer={widgetTransformer.toDays}
          tooltipTransformer={(days) => widgetTransformer.toDays(days, false)}
        />
        <TemplateInsightWidget
          value={percentageStartedOnTime}
          title={translate(
            'reports.template_insight.widget.started_on_time.title',
          )}
          tooltip={translate(
            'reports.template_insight.widget.started_on_time.description',
          )}
          noDataMsg="No Data"
          cmpValue={compareInfo?.percentageStartedOnTime}
          headerTransformer={widgetTransformer.toPercent}
          tooltipTransformer={widgetTransformer.toPercent}
        />
        <TemplateInsightWidget
          value={averageStartingDelay}
          title={translate(
            'reports.template_insight.widget.avg_starting_delay.title',
          )}
          tooltip={translate(
            'reports.template_insight.widget.avg_starting_delay.description',
          )}
          noDataMsg="No Data"
          cmpValue={compareInfo?.averageStartingDelay}
          headerTransformer={widgetTransformer.toDeviation}
          footerTransformer={(days) =>
            widgetTransformer.toDeviation(days, false)
          }
          tooltipTransformer={(days) => widgetTransformer.toDays(days, false)}
        />
        <TemplateInsightWidget
          value={percentageCompletedOnTime}
          title={translate(
            'reports.template_insight.widget.completed_on_time.title',
          )}
          tooltip={translate(
            'reports.template_insight.widget.completed_on_time.description',
          )}
          noDataMsg="No Data"
          cmpValue={compareInfo?.percentageCompletedOnTime}
          headerTransformer={widgetTransformer.toPercent}
          tooltipTransformer={widgetTransformer.toPercent}
        />
        <TemplateInsightWidget
          value={averageCompletionDelay}
          title={translate(
            'reports.template_insight.widget.avg_completion_delay.title',
          )}
          tooltip={translate(
            'reports.template_insight.widget.avg_completion_delay.description',
          )}
          noDataMsg="No Data"
          cmpValue={compareInfo?.averageCompletionDelay}
          headerTransformer={widgetTransformer.toDeviation}
          footerTransformer={(days) =>
            widgetTransformer.toDeviation(days, false)
          }
          tooltipTransformer={(days) => widgetTransformer.toDays(days, false)}
        />
      </WidgetContainer>
      <ChartWithZoom
        chartOptions={chartOptions}
        title={translate(
          'reports.template_insight.by-template.by-project.chart.title',
        )}
        tooltip={translate(
          'reports.template_insight.by-template.by-project.chart.description',
        )}
        isLoading={projectsReport[0] === 'loading'}
        renderEmpty={() =>
          DefaultEmptyChart({
            noDataKey: 'report.chart.empty.template.project',
          })
        }
      />
      {projectsReport[0] !== 'loading' && (
        <TimelineViewTable
          projectsReportRecords={records}
          renderEmpty={() =>
            DefaultEmptyTable({
              noDataKey: 'reports.template_insight.table.empty.template',
            })
          }
        />
      )}
      {projectsReport[0] === 'loading' && <TableSkeleton columnCount={8} />}
    </Wrapper>
  );
};
